import type { CitationRef } from '../components/ChatAnswerWithCitations'
import type { StreamPhase, WebRef } from '../components/ChatMessageExtras'
import type { HarnessRoundLog } from '../api/chatStream'
import type { AgentStep } from './agentSteps'

export type CachedMsg = {
  id: number
  role: string
  content: string
  created_at: string
  qa_case_label?: string
  qa_gold_item_ids?: number[]
  chunk_refs?: unknown[] | null
  answer_figures?: unknown[] | null
  thinking_content?: string | null
  web_refs?: WebRef[] | null
  citation_refs?: CitationRef[] | null
  harness_rounds?: HarnessRoundLog[] | null
  agent_steps?: AgentStep[] | null
}

export type ConvLiveState = {
  messages: CachedMsg[]
  sending: boolean
  streamingId: number | null
  streamPhase: StreamPhase
}

export const convLiveCache = new Map<number, ConvLiveState>()
export const streamAbortCache = new Map<number, AbortController>()

export const emptyConvLive = (): ConvLiveState => ({
  messages: [],
  sending: false,
  streamingId: null,
  streamPhase: 'done',
})

/** 服务端拉取与内存缓存合并：避免回页时 DB 尚未落库导致回答被空数据覆盖。 */
export function mergeConvMessages(cached: CachedMsg[] | undefined, server: CachedMsg[]): CachedMsg[] {
  if (!cached?.length) return server
  if (!server.length) return cached

  const cachedLast = cached[cached.length - 1]
  const serverLast = server[server.length - 1]

  if (
    cachedLast?.role === 'assistant' &&
    (cachedLast.content?.trim() || cachedLast.thinking_content?.trim()) &&
    serverLast?.role === 'assistant' &&
    !serverLast.content?.trim() &&
    !(serverLast.thinking_content as string | null | undefined)?.trim()
  ) {
    return cached
  }

  if (server.length >= cached.length) {
    return server.map((sm) => {
      const cm = cached.find((m) => m.id === sm.id)
      if (!cm) return sm
      const smFig = (sm as CachedMsg).answer_figures
      const cmFig = (cm as CachedMsg).answer_figures
      const answer_figures =
        Array.isArray(smFig) && smFig.length
          ? smFig
          : Array.isArray(cmFig) && cmFig.length
            ? cmFig
            : smFig ?? cmFig ?? null
      return { ...sm, answer_figures }
    })
  }
  if (cached.length > server.length) {
    const serverIds = new Set(server.map((m) => m.id))
    const tail = cached.filter((m) => !serverIds.has(m.id))
    return [...server, ...tail]
  }
  return server
}
